// ============================================================================
// GumaHUB Branding Service
// Frontend API client for white-label branding management
// ============================================================================

import { api } from '@gumahub/shared';

// ============================================================================
// Types
// ============================================================================

export interface BrandingConfig {
  brandingId: string;
  clientId: string;
  brandSlug: string | null;
  companyDisplayName: string | null;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  iconUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  loginBgImageUrl: string | null;
  loginTitle: string | null;
  loginSubtitle: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBrandingData {
  client_id: string;
  brand_slug?: string;
  company_display_name?: string;
  logo_url?: string;
  logo_dark_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  login_bg_image_url?: string;
  login_title?: string;
  login_subtitle?: string;
}

export interface UpdateBrandingData {
  brand_slug?: string;
  company_display_name?: string;
  logo_url?: string;
  logo_dark_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  login_bg_image_url?: string;
  login_title?: string;
  login_subtitle?: string;
}

export type LogoType = 'logo' | 'logo_dark' | 'icon' | 'favicon' | 'login_bg';

export interface BrandingWithClient extends BrandingConfig {
  clientName: string;
  clientTaxNumber: string | null;
}

export interface ClientInfo {
  clientId: string;
  clientCode: string;
  companyName: string;
  taxNumber: string | null;
  hasBranding: boolean;
}

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Get branding by slug (public - for login page)
 */
export async function getBrandingBySlug(slug: string) {
  return api.get<BrandingConfig>(`/branding/public/${slug}`);
}

/**
 * Get all branding configurations (platform admin only)
 */
export async function getAllBranding() {
  return api.get<BrandingWithClient[]>('/branding/all');
}

/**
 * Get all clients for dropdown (platform admin only)
 */
export async function getAllClients() {
  return api.get<ClientInfo[]>('/branding/clients');
}

/**
 * Get current user's client branding
 */
export async function getMyBranding() {
  return api.get<BrandingConfig | null>('/branding/me');
}

/**
 * Get branding for a specific client (platform admin)
 */
export async function getBrandingByClientId(clientId: string) {
  return api.get<BrandingConfig>(`/branding/client/${clientId}`);
}

/**
 * Create new branding configuration
 */
export async function createBranding(data: CreateBrandingData) {
  return api.post<BrandingConfig>('/branding', data);
}

/**
 * Update existing branding configuration
 */
export async function updateBranding(brandingId: string, data: UpdateBrandingData) {
  return api.put<BrandingConfig>(`/branding/${brandingId}`, data);
}

/**
 * Upload logo/background image
 */
export async function uploadLogo(brandingId: string, file: File, type: LogoType) {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<BrandingConfig>(`/branding/${brandingId}/logo?type=${type}`, formData);
}

/**
 * Delete branding (soft delete)
 */
export async function deleteBranding(brandingId: string) {
  return api.delete(`/branding/${brandingId}`);
}

/**
 * Check if slug is available
 */
export async function checkSlugAvailability(slug: string, excludeBrandingId?: string) {
  const params = excludeBrandingId ? `?exclude=${excludeBrandingId}` : '';
  return api.get<{ available: boolean }>(`/branding/check-slug/${slug}${params}`);
}

// ============================================================================
// Export
// ============================================================================

export const brandingService = {
  getBrandingBySlug,
  getMyBranding,
  getBrandingByClientId,
  getAllBranding,
  getAllClients,
  createBranding,
  updateBranding,
  uploadLogo,
  deleteBranding,
  checkSlugAvailability,
};

export default brandingService;
